'use client'

// src/theme.ts
import {
  extendTheme,
  withDefaultColorScheme
} from "@chakra-ui/react";

// src/foundations/colors.ts
var colors = {
  black: "#0e1012",
  primary: {
    "50": "#f6f7fd",
    "100": "#dcdff5",
    "200": "#bec3ed",
    "300": "#9aa1e2",
    "400": "#858edd",
    "500": "#6b75d0",
    "600": "#5a63af",
    "700": "#484f8d",
    "800": "#3d4377",
    "900": "#2c3056"
  },
  grayAlpha: {
    "50": "rgba(249, 249, 250, 0.5)",
    "100": "rgba(241, 241, 243, 0.5)",
    "200": "rgba(231, 231, 234, 0.5)",
    "300": "rgba(211, 212, 216, 0.5)",
    "400": "rgba(171, 172, 181, 0.5)",
    "500": "rgba(124, 126, 139, 0.5)",
    "600": "rgba(81, 84, 101, 0.5)",
    "700": "rgba(52, 54, 67, 0.5)",
    "800": "rgba(30, 32, 39, 0.5)",
    "900": "rgba(24, 25, 31, 0.5)"
  },
  gray: {
    "50": "#f9f9fa",
    "100": "#f1f1f3",
    "200": "#e7e7ea",
    "300": "#d3d4d8",
    "400": "#abacb5",
    "500": "#7c7e8b",
    "600": "#515465",
    "700": "#343643",
    "800": "#1e2027",
    "900": "#18191f"
  },
  purple: {
    "50": "#f9f6fd",
    "100": "#e5daf8",
    "200": "#d3bef4",
    "300": "#b795ec",
    "400": "#a379e7",
    "500": "#8952e0",
    "600": "#7434db",
    "700": "#6023c0",
    "800": "#4f1d9e",
    "900": "#3b1676"
  },
  pink: {
    "50": "#fdf5f9",
    "100": "#f8d9e7",
    "200": "#f3b9d3",
    "300": "#eb8db8",
    "400": "#e56ba2",
    "500": "#dc3882",
    "600": "#c4246c",
    "700": "#a01d58",
    "800": "#7d1745",
    "900": "#5d1133"
  },
  red: {
    "50": "#fdf6f5",
    "100": "#f8d9d8",
    "200": "#f1b8b4",
    "300": "#e98d87",
    "400": "#e4726c",
    "500": "#dc4a41",
    "600": "#d2140a",
    "700": "#ac0900",
    "800": "#930800",
    "900": "#6d0600"
  },
  orange: {
    "50": "#fdfaf6",
    "100": "#f9ebdb",
    "200": "#f1d4b1",
    "300": "#e6b273",
    "400": "#dc9239",
    "500": "#c37b24",
    "600": "#a5681e",
    "700": "#835318",
    "800": "#674113",
    "900": "#553610"
  },
  yellow: {
    "50": "#fffefb",
    "100": "#fff8e9",
    "200": "#feecbd",
    "300": "#fddc87",
    "400": "#fbc434",
    "500": "#d2a01e",
    "600": "#a88018",
    "700": "#836413",
    "800": "#624b0e",
    "900": "#513e0c"
  },
  green: {
    "50": "#f7fdfb",
    "100": "#d2f2e7",
    "200": "#9fe3cd",
    "300": "#64d2ad",
    "400": "#1dbd88",
    "500": "#0ea371",
    "600": "#0c875e",
    "700": "#096949",
    "800": "#07563c",
    "900": "#064731"
  },
  teal: {
    "50": "#f1fcfc",
    "100": "#c0f1f4",
    "200": "#84e4e9",
    "300": "#2dd1da",
    "400": "#22b2ba",
    "500": "#1d979e",
    "600": "#187b80",
    "700": "#125f64",
    "800": "#0f5053",
    "900": "#0d4244"
  },
  cyan: {
    "50": "#f4fbfd",
    "100": "#d0eef7",
    "200": "#bae7f3",
    "300": "#a2deee",
    "400": "#53c2e1",
    "500": "#2ab4d9",
    "600": "#24a2c4",
    "700": "#1e86a2",
    "800": "#196e85",
    "900": "#135567"
  },
  blue: {
    "50": "#f1f6fd",
    "100": "#cde0f6",
    "200": "#a8c8f0",
    "300": "#7fafe8",
    "400": "#5896e1",
    "500": "#347fdb",
    "600": "#236abf",
    "700": "#1b5192",
    "800": "#164278",
    "900": "#123662"
  },
  indigo: {
    "50": "#f8f7fc",
    "100": "#e1ddf5",
    "200": "#c8c0ec",
    "300": "#a89de2",
    "400": "#9789dc",
    "500": "#7f6ed4",
    "600": "#6a58c9",
    "700": "#5546a1",
    "800": "#483c88",
    "900": "#342b62"
  }
};
var colors_default = colors;

// src/foundations/typography.ts
var fonts = {
  heading: "InterVariable, sans-serif",
  body: "InterVariable, sans-serif"
};
var fontSizes = {
  xs: "0.75rem",
  sm: "0.8125rem",
  md: "0.875rem",
  lg: "1rem",
  xl: "1.125rem",
  "2xl": "1.5rem",
  "3xl": "1.875rem",
  "4xl": "2.25rem",
  "5xl": "3rem",
  "6xl": "3.75rem",
  "7xl": "4.5rem",
  "8xl": "6rem",
  "9xl": "8rem"
};
var textStyles = {};

// src/foundations/sizes.ts
var sizes = {
  container: {
    sm: "30em",
    md: "48em",
    lg: "62em",
    xl: "80em",
    "2xl": "96em"
  }
};
var sizes_default = sizes;

// src/foundations/shadows.ts
import { transparentize } from "@chakra-ui/theme-tools";
var shadows = {
  xs: "rgb(0 0 0 / 7%) 0px 1px 1px",
  "dark-xs": "rgb(0 0 0 / 7%) 0px 1px 1px",
  outline: `0 0 0 2px ${transparentize(colors_default.primary[500], 0.6)({ colors: colors_default })}`
};
var shadows_default = shadows;

// src/foundations/semantic-tokens.ts
var semantic_tokens_default = {
  colors: {
    muted: {
      default: "gray.500",
      _dark: "gray.400"
    }
  }
};

// src/components/alert.ts
import { alertAnatomy as parts } from "@chakra-ui/anatomy";
import {
  createMultiStyleConfigHelpers,
  cssVar
} from "@chakra-ui/styled-system";
import { transparentize as transparentize2 } from "@chakra-ui/theme-tools";
var { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(parts.keys);
var $fg = cssVar("alert-fg");
var $bg = cssVar("alert-bg");
var $b = cssVar("alert-border");
function getBg(props) {
  const { theme: theme2, colorScheme: c } = props;
  const darkBg = transparentize2(`${c}.600`, 0.16)(theme2);
  return {
    light: `colors.${c}.100`,
    dark: darkBg
  };
}
function getBorder(props) {
  const { theme: theme2, colorScheme: c } = props;
  const darkBg = transparentize2(`${c}.300`, 0.16)(theme2);
  return {
    light: `colors.${c}.200`,
    dark: darkBg
  };
}
var variantSubtle = definePartsStyle((props) => {
  const { colorScheme: c } = props;
  const bg = getBg(props);
  const border = getBorder(props);
  return {
    container: {
      borderWidth: "1px",
      [$fg.variable]: `colors.${c}.500`,
      [$bg.variable]: bg.light,
      [$b.variable]: border.light,
      _dark: {
        [$fg.variable]: `colors.${c}.500`,
        [$bg.variable]: bg.dark,
        [$b.variable]: border.dark
      }
    }
  };
});
var baseStyle = definePartsStyle(() => {
  return {
    container: {
      borderColor: $b.reference,
      borderRadius: "md",
      fontSize: "sm"
    },
    icon: {
      w: 4,
      h: 5
    },
    title: {
      fontSize: "sm",
      fontWeight: "medium"
    },
    description: {
      color: "muted"
    }
  };
});
var alert_default = defineMultiStyleConfig({
  defaultProps: {
    size: "sm",
    colorScheme: "primary"
  },
  baseStyle,
  variants: {
    subtle: variantSubtle
  }
});

// src/components/button.ts
import { defineStyle, defineStyleConfig } from "@chakra-ui/styled-system";
var variantSolid = defineStyle((props) => {
  const { colorScheme: c, colorMode } = props;
  let color = `${c}.500`;
  let hoverColor = `${c}.600`;
  if (c === "neutral" && colorMode === "dark") {
    color = "white";
    hoverColor = "whiteAlpha.800";
  } else if (c === "neutral") {
    color = "black";
    hoverColor = "blackAlpha.800";
  }
  return {
    borderWidth: "1px",
    borderColor: color,
    boxShadow: "xs",
    bg: color,
    color: "white",
    fontWeight: "medium",
    _hover: {
      borderColor: hoverColor,
      bg: hoverColor
    },
    _dark: {
      boxShadow: "dark-xs"
    }
  };
});
var variantGhost = defineStyle((props) => {
  return {
    fontWeight: "medium",
    _checked: {
      bg: "blackAlpha.100"
    },
    _dark: {
      _checked: {
        bg: "whiteAlpha.100"
      }
    }
  };
});
var variantOutline = defineStyle((props) => {
  const { colorScheme: c } = props;
  if (c === "gray") {
    return {
      fontWeight: "medium"
    };
  }
  return {
    fontWeight: "medium",
    color: `${c}.500`,
    _hover: {
      color: `${c}.400`,
      bg: "blackAlpha.50"
    },
    _dark: {
      color: `${c}.500`,
      _hover: {
        color: `${c}.400`,
        bg: "whiteAlpha.50"
      }
    }
  };
});
var variantPrimary = defineStyle((props) => {
  return variantSolid({ ...props, colorScheme: "primary" });
});
var variantSecondary = defineStyle({
  borderWidth: "1px",
  borderColor: "gray.200",
  boxShadow: "xs",
  bg: "white",
  fontWeight: "medium",
  color: "inherit",
  _hover: {
    borderColor: "gray.300",
    bg: "gray.50"
  },
  _dark: {
    boxShadow: "dark-xs",
    borderColor: "gray.600",
    bg: "gray.700",
    _hover: {
      borderColor: "gray.500",
      bg: "gray.600"
    }
  }
});
var variantTertiary = defineStyle({
  borderWidth: "1px",
  borderColor: "gray.200",
  bg: "gray.200",
  color: "black",
  fontWeight: "medium",
  _hover: {
    borderColor: "gray.300",
    bg: "gray.200"
  },
  _dark: {
    borderColor: "gray.700",
    bg: "gray.700",
    color: "white",
    _hover: {
      borderColor: "gray.600"
    }
  }
});
var sizes2 = {
  lg: defineStyle({
    h: "8",
    minW: "10",
    fontSize: "md",
    px: "4",
    rounded: "6"
  }),
  md: defineStyle({
    h: "7",
    minW: "8",
    fontSize: "sm",
    px: "3",
    rounded: "4"
  }),
  sm: defineStyle({
    h: "6",
    minW: "6",
    fontSize: "sm",
    px: "2",
    rounded: "3"
  }),
  xs: defineStyle({
    h: "5",
    minW: "4",
    fontSize: "xs",
    px: "1",
    rounded: "3"
  })
};
var button_default = defineStyleConfig({
  defaultProps: {
    size: "md",
    variant: "secondary"
  },
  variants: {
    solid: variantSolid,
    outline: variantOutline,
    ghost: variantGhost,
    primary: variantPrimary,
    secondary: variantSecondary,
    tertiary: variantTertiary
  },
  sizes: sizes2
});

// src/components/card.ts
import { anatomy } from "@chakra-ui/anatomy";
import { createMultiStyleConfigHelpers as createMultiStyleConfigHelpers2, cssVar as cssVar2 } from "@chakra-ui/styled-system";
var parts2 = anatomy("card").parts(
  "container",
  "header",
  "title",
  "subtitle",
  "body",
  "footer"
);
var { definePartsStyle: definePartsStyle2 } = createMultiStyleConfigHelpers2(parts2.keys);
var $bg2 = cssVar2("card-bg");
var $padding = cssVar2("card-padding");
var $shadow = cssVar2("card-shadow");
var $radius = cssVar2("card-radius");
var $border = cssVar2("card-border-width", "0");
var $borderColor = cssVar2("card-border-color");
var sizes3 = {
  sm: definePartsStyle2({
    container: {
      [$radius.variable]: "radii.base",
      [$padding.variable]: "space.3"
    }
  }),
  md: definePartsStyle2({
    container: {
      [$radius.variable]: "radii.md",
      [$padding.variable]: "space.4"
    }
  }),
  lg: definePartsStyle2({
    container: {
      [$radius.variable]: "radii.xl",
      [$padding.variable]: "space.6"
    }
  })
};
var variantShadow = definePartsStyle2((props) => {
  const { isHoverable } = props;
  return {
    container: {
      [$borderColor.variable]: "colors.blackAlpha.200",
      [$border.variable]: "1px",
      [$shadow.variable]: "shadows.sm",
      _hover: isHoverable ? {
        [$borderColor.variable]: "colors.blackAlpha.300"
      } : {},
      _dark: {
        [$bg2.variable]: "colors.gray.700",
        [$borderColor.variable]: "colors.whiteAlpha.50",
        _hover: isHoverable ? {
          [$borderColor.variable]: "colors.whiteAlpha.300"
        } : {}
      }
    }
  };
});
var card_default = {
  parts: parts2.keys,
  defaultProps: {
    variant: "elevated"
  },
  variants: {
    elevated: variantShadow,
    shadow: variantShadow
  },
  sizes: sizes3
};

// src/components/checkbox.ts
var checkbox_default = {
  baseStyle: (props) => {
    const { colorScheme } = props;
    return {
      control: {
        _checked: {
          borderColor: `${colorScheme}.500`,
          bg: `${colorScheme}.500`,
          color: "white"
        }
      }
    };
  }
};

// src/components/close-button.ts
var defaultProps = {
  size: "sm"
};
var close_button_default = {
  defaultProps
};

// src/components/drawer.ts
import { drawerAnatomy as parts3 } from "@chakra-ui/anatomy";
import {
  createMultiStyleConfigHelpers as createMultiStyleConfigHelpers3,
  cssVar as cssVar3,
  defineStyle as defineStyle2
} from "@chakra-ui/styled-system";
import { runIfFn } from "@chakra-ui/utils";
var { defineMultiStyleConfig: defineMultiStyleConfig2, definePartsStyle: definePartsStyle3 } = createMultiStyleConfigHelpers3(parts3.keys);
var $bg3 = cssVar3("drawer-bg");
var baseStyleOverlay = defineStyle2({
  bg: "blackAlpha.50"
});
var baseStyleDialog = defineStyle2((props) => {
  return {
    borderRadius: "md",
    margin: "8px",
    borderWidth: "1px",
    borderColor: "chakra-border-color",
    backdropFilter: "blur(10px) contrast(100%) saturate(100%) brightness(200%)",
    [$bg3.variable]: "colors.whiteAlpha.800",
    fontSize: "md",
    _dark: {
      backdropFilter: "blur(10px) saturate(190%) contrast(70%) brightness(80%)",
      [$bg3.variable]: "colors.grayAlpha.700"
    }
  };
});
var baseStyleHeader = defineStyle2({
  px: "4",
  py: "3",
  fontSize: "md",
  fontWeight: "semibold"
});
var baseStyleCloseButton = defineStyle2({
  position: "absolute",
  top: "2",
  insetEnd: "3"
});
var baseStyleBody = defineStyle2((props) => {
  return {
    px: "4",
    py: "2"
  };
});
var baseStyleFooter = defineStyle2({
  px: "3",
  py: "3"
});
var baseStyle2 = definePartsStyle3((props) => ({
  overlay: baseStyleOverlay,
  dialog: runIfFn(baseStyleDialog, props),
  header: baseStyleHeader,
  closeButton: baseStyleCloseButton,
  body: runIfFn(baseStyleBody, props),
  footer: baseStyleFooter
}));
var modalTheme = defineMultiStyleConfig2({
  baseStyle: baseStyle2
});
var drawer_default = modalTheme;

// src/components/heading.ts
var heading_default = {
  defaultProps: {
    size: "lg"
  }
};

// src/components/kbd.ts
import {
  cssVar as cssVar4,
  defineStyle as defineStyle3,
  defineStyleConfig as defineStyleConfig2
} from "@chakra-ui/styled-system";
var $bg4 = cssVar4("kbd-bg");
var baseStyle3 = defineStyle3({
  [$bg4.variable]: "colors.blackAlpha.300",
  _dark: {
    [$bg4.variable]: "colors.whiteAlpha.300"
  },
  bg: $bg4.reference,
  borderRadius: "md",
  borderWidth: "0",
  borderBottomWidth: "0",
  padding: "1px",
  minW: "20px",
  textAlign: "center",
  ":last-child": {
    mr: 0
  }
});
var kbdTheme = defineStyleConfig2({
  defaultProps: {
    variant: "solid"
  },
  baseStyle: baseStyle3
});
var kbd_default = kbdTheme;

// src/components/menu.ts
import { menuAnatomy as parts4 } from "@chakra-ui/anatomy";
import {
  createMultiStyleConfigHelpers as createMultiStyleConfigHelpers4,
  cssVar as cssVar5,
  defineStyle as defineStyle4
} from "@chakra-ui/styled-system";
var { defineMultiStyleConfig: defineMultiStyleConfig3, definePartsStyle: definePartsStyle4 } = createMultiStyleConfigHelpers4(parts4.keys);
var $bg5 = cssVar5("menu-bg");
var $shadow2 = cssVar5("menu-shadow");
var baseStyleList = defineStyle4({
  [$bg5.variable]: "colors.whiteAlpha.500",
  [$shadow2.variable]: "shadows.lg",
  backdropFilter: "blur(10px) contrast(100%) saturate(190%)",
  _dark: {
    [$bg5.variable]: "colors.grayAlpha.700",
    [$shadow2.variable]: "shadows.dark-lg",
    backdropFilter: "blur(10px) saturate(190%) contrast(70%) brightness(80%)"
  },
  py: "1",
  borderRadius: "md",
  borderWidth: "1px",
  bg: $bg5.reference,
  boxShadow: $shadow2.reference
});
var baseStyleItem = defineStyle4({
  py: "1.5",
  px: "3",
  mx: "1",
  rounded: "md",
  fontSize: "sm",
  width: "calc(100% - 8px)",
  boxSizing: "border-box",
  bg: "transparent",
  _hover: {
    bg: $bg5.reference,
    [$bg5.variable]: "colors.grayAlpha.300"
  },
  _focus: {
    bg: $bg5.reference,
    [$bg5.variable]: "colors.grayAlpha.300"
  },
  _active: {
    bg: $bg5.reference,
    [$bg5.variable]: "colors.grayAlpha.300"
  },
  _expanded: {
    bg: $bg5.reference,
    [$bg5.variable]: "colors.grayAlpha.300"
  }
});
var baseStyleGroupTitle = defineStyle4({
  mx: 4,
  my: 2,
  fontWeight: "semibold",
  fontSize: "sm"
});
var baseStyleButton = defineStyle4({
  transitionProperty: "common",
  transitionDuration: "normal"
});
var baseStyleDivider = defineStyle4({
  my: "1"
});
var baseStyle4 = definePartsStyle4({
  button: baseStyleButton,
  list: baseStyleList,
  item: baseStyleItem,
  groupTitle: baseStyleGroupTitle,
  divider: baseStyleDivider
});
var menuTheme = defineMultiStyleConfig3({
  baseStyle: baseStyle4
});
var menu_default = menuTheme;

// src/components/modal.ts
import { modalAnatomy as parts5 } from "@chakra-ui/anatomy";
import {
  createMultiStyleConfigHelpers as createMultiStyleConfigHelpers5,
  defineStyle as defineStyle5
} from "@chakra-ui/styled-system";
import { runIfFn as runIfFn2 } from "@chakra-ui/utils";
var { defineMultiStyleConfig: defineMultiStyleConfig4, definePartsStyle: definePartsStyle5 } = createMultiStyleConfigHelpers5(parts5.keys);
var baseStyleOverlay2 = defineStyle5({
  bg: "blackAlpha.50"
});
var baseStyleDialog2 = defineStyle5((props) => {
  return {
    bg: "whiteAlpha.800",
    backdropFilter: "blur(10px) contrast(100%) saturate(100%) brightness(200%)",
    fontSize: "md",
    _dark: {
      bg: "grayAlpha.700",
      boxShadow: "dark-lg",
      backdropFilter: "blur(10px) saturate(190%) contrast(70%) brightness(80%)"
    }
  };
});
var baseStyleHeader2 = defineStyle5({
  px: "4",
  py: "3",
  fontSize: "md",
  fontWeight: "semibold"
});
var baseStyleCloseButton2 = defineStyle5({
  position: "absolute",
  top: "2",
  insetEnd: "3"
});
var baseStyleBody2 = defineStyle5((props) => {
  return {
    px: "4",
    py: "2"
  };
});
var baseStyleFooter2 = defineStyle5({
  px: "3",
  py: "3"
});
var baseStyle5 = definePartsStyle5((props) => ({
  overlay: baseStyleOverlay2,
  dialog: runIfFn2(baseStyleDialog2, props),
  header: baseStyleHeader2,
  closeButton: baseStyleCloseButton2,
  body: runIfFn2(baseStyleBody2, props),
  footer: baseStyleFooter2
}));
var modalTheme2 = defineMultiStyleConfig4({
  baseStyle: baseStyle5,
  defaultProps: { size: "md" }
});
var modal_default = modalTheme2;

// src/components/progress.ts
import { progressAnatomy as parts6 } from "@chakra-ui/anatomy";
import { createMultiStyleConfigHelpers as createMultiStyleConfigHelpers6 } from "@chakra-ui/styled-system";
var { defineMultiStyleConfig: defineMultiStyleConfig5 } = createMultiStyleConfigHelpers6(parts6.keys);
var progress_default = defineMultiStyleConfig5({
  defaultProps: {
    colorScheme: "primary"
  },
  baseStyle: (props) => {
    const { colorScheme } = props;
    return {
      track: {
        borderRadius: "md",
        bg: "gray.200",
        _dark: {
          bg: "gray.800"
        }
      },
      filledTrack: {
        bg: `${colorScheme}.500`
      }
    };
  }
});

// src/components/popover.ts
import { popoverAnatomy as parts7 } from "@chakra-ui/anatomy";
import {
  createMultiStyleConfigHelpers as createMultiStyleConfigHelpers7,
  cssVar as cssVar6,
  defineStyle as defineStyle6
} from "@chakra-ui/styled-system";
import { runIfFn as runIfFn3 } from "@chakra-ui/utils";
var { defineMultiStyleConfig: defineMultiStyleConfig6, definePartsStyle: definePartsStyle6 } = createMultiStyleConfigHelpers7(parts7.keys);
var $popperBg = cssVar6("popper-bg");
var $arrowBg = cssVar6("popper-arrow-bg");
var $arrowShadowColor = cssVar6("popper-arrow-shadow-color");
var baseStyleContent = defineStyle6({
  [$popperBg.variable]: `colors.whiteAlpha.800`,
  bg: $popperBg.reference,
  [$arrowBg.variable]: $popperBg.reference,
  [$arrowShadowColor.variable]: `colors.gray.200`,
  backdropFilter: "blur(10px) contrast(100%) saturate(100%) brightness(200%)",
  _dark: {
    [$popperBg.variable]: `colors.grayAlpha.700`,
    [$arrowShadowColor.variable]: `colors.whiteAlpha.300`,
    backdropFilter: "blur(10px) saturate(190%) contrast(70%) brightness(80%)"
  }
});
var baseStyle6 = definePartsStyle6((props) => ({
  content: runIfFn3(baseStyleContent, props)
}));
var popoverTheme = defineMultiStyleConfig6({
  baseStyle: baseStyle6,
  defaultProps: { size: "md" }
});
var popover_default = popoverTheme;

// src/components/radio.ts
var radio_default = {
  baseStyle: (props) => {
    const { colorScheme } = props;
    return {
      control: {
        _checked: {
          borderColor: `${colorScheme}.500`,
          bg: `${colorScheme}.500`,
          color: "white"
        }
      }
    };
  }
};

// src/components/slider.ts
var slider_default = {
  defaultProps: {
    colorScheme: "primary"
  },
  baseStyle: (props) => {
    const { colorScheme } = props;
    return {
      filledTrack: {
        bg: `${colorScheme}.500`
      }
    };
  }
};

// src/components/switch.ts
import { switchAnatomy as parts8 } from "@chakra-ui/anatomy";
import {
  createMultiStyleConfigHelpers as createMultiStyleConfigHelpers8,
  defineStyle as defineStyle7
} from "@chakra-ui/styled-system";
import { cssVar as cssVar7 } from "@chakra-ui/theme-tools";
var { defineMultiStyleConfig: defineMultiStyleConfig7, definePartsStyle: definePartsStyle7 } = createMultiStyleConfigHelpers8(parts8.keys);
var $bg6 = cssVar7("switch-bg");
var baseStyleTrack = defineStyle7((props) => {
  const { colorScheme: c } = props;
  return {
    [$bg6.variable]: "colors.gray.300",
    _dark: {
      [$bg6.variable]: "colors.whiteAlpha.300"
    },
    _checked: {
      [$bg6.variable]: `colors.${c}.500`,
      _dark: {
        [$bg6.variable]: `colors.${c}.500`
      }
    },
    bg: $bg6.reference
  };
});
var baseStyleThumb = defineStyle7({
  bg: "white",
  _dark: {
    bg: "blackAlpha.800",
    _checked: {
      bg: "white"
    }
  }
});
var baseStyle7 = definePartsStyle7((props) => ({
  track: baseStyleTrack(props),
  thumb: baseStyleThumb
}));
var switchTheme = defineMultiStyleConfig7({
  baseStyle: baseStyle7,
  defaultProps: {
    size: "md",
    colorScheme: "primary"
  }
});
var switch_default = switchTheme;

// src/components/tooltip.ts
import { cssVar as cssVar8 } from "@chakra-ui/theme-tools";
var $bg7 = cssVar8("tooltip-bg");
var $fg2 = cssVar8("tooltip-fg");
var $arrowBg2 = cssVar8("popper-arrow-bg");
var baseStyle8 = (props) => {
  return {
    display: "flex",
    [$bg7.variable]: "colors.white",
    [$fg2.variable]: "colors.blackAlpha.900",
    _dark: {
      [$bg7.variable]: "colors.gray.700",
      [$fg2.variable]: "colors.whiteAlpha.900"
    },
    px: "8px",
    py: "2px",
    bg: [$bg7.reference],
    [$arrowBg2.variable]: [$bg7.reference],
    borderRadius: "sm",
    fontWeight: "medium",
    fontSize: "xs",
    boxShadow: "md",
    maxW: "320px",
    zIndex: "tooltip",
    borderWidth: "1px"
  };
};
var tooltip_default = {
  baseStyle: baseStyle8
};

// src/components/form.ts
import { inputAnatomy as parts9 } from "@chakra-ui/anatomy";
import {
  createMultiStyleConfigHelpers as createMultiStyleConfigHelpers9,
  cssVar as cssVar9,
  defineStyle as defineStyle8,
  defineStyleConfig as defineStyleConfig3
} from "@chakra-ui/styled-system";
import { getColor, mode } from "@chakra-ui/theme-tools";
var { definePartsStyle: definePartsStyle8 } = createMultiStyleConfigHelpers9(parts9.keys);
function getDefaults(props) {
  const { focusBorderColor: fc, errorBorderColor: ec } = props;
  return {
    focusBorderColor: fc || mode("blue.500", "blue.300")(props),
    errorBorderColor: ec || mode("red.500", "red.300")(props)
  };
}
var $borderRadius = cssVar9("input-border-radius");
var $height = cssVar9("input-height");
var $padding2 = cssVar9("input-padding");
var variantOutline2 = definePartsStyle8((props) => {
  const { theme: theme2 } = props;
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);
  return {
    field: {
      border: "1px solid var(--chakra-colors-chakra-border-color)",
      bg: "inherit",
      _hover: {
        borderColor: "gray.300"
      },
      _invalid: {
        borderColor: getColor(theme2, ec),
        boxShadow: "none"
      },
      _focusVisible: {
        zIndex: 1,
        borderColor: getColor(theme2, fc),
        boxShadow: "none"
      },
      _dark: {
        bg: "gray.900",
        borderColor: "chakra-border-color",
        _hover: {
          borderColor: "whiteAlpha.400"
        },
        _invalid: {
          borderColor: getColor(theme2, ec)
        },
        _focusVisible: {
          borderColor: getColor(theme2, fc)
        }
      }
    },
    addon: {
      borderColor: "inherit",
      bg: "gray.100",
      _dark: {
        borderColor: "whiteAlpha.50",
        bg: "whiteAlpha.300"
      }
    }
  };
});
var sizes4 = {
  sm: {
    field: {
      [$borderRadius.variable]: "radii.sm",
      [$height.variable]: "sizes.7"
    },
    addon: {
      [$borderRadius.variable]: "radii.sm",
      [$height.variable]: "sizes.7"
    }
  },
  md: {
    field: {
      [$padding2.variable]: "space.3",
      [$height.variable]: "sizes.9"
    },
    addon: {
      [$padding2.variable]: "space.3",
      [$height.variable]: "sizes.9"
    }
  }
};
var Input = defineStyleConfig3({
  defaultProps: {
    variant: "outline",
    size: "sm",
    /* @ts-expect-error */
    focusBorderColor: "primary.500"
  },
  variants: {
    outline: variantOutline2
  },
  sizes: sizes4
});
var formLabelTheme = defineStyleConfig3({
  baseStyle: {
    mb: 1
  },
  variants: {
    horizontal: {
      mb: 0,
      marginStart: "0.5rem"
    }
  }
});
var inputTheme = Input;
var numberInputTheme = Input;
var pinInputTheme = defineStyleConfig3({
  defaultProps: {
    /* @ts-expect-error */
    focusBorderColor: "primary.500"
  },
  variants: {
    outline: variantOutline2
  },
  sizes: sizes4
});
var textareaTheme = defineStyleConfig3({
  defaultProps: {
    /* @ts-expect-error */
    focusBorderColor: "primary.500"
  },
  variants: {
    outline: defineStyle8(
      (props) => {
        var _a, _b;
        return (_b = (_a = inputTheme.variants) == null ? void 0 : _a.outline(props).field) != null ? _b : {};
      }
    )
  }
});
var nativeSelectTheme = inputTheme;

// src/components/index.ts
var components_default = {
  Alert: alert_default,
  Button: button_default,
  Card: card_default,
  Checkbox: checkbox_default,
  CloseButton: close_button_default,
  Drawer: drawer_default,
  Heading: heading_default,
  Kbd: kbd_default,
  Menu: menu_default,
  Modal: modal_default,
  Progress: progress_default,
  Popover: popover_default,
  Radio: radio_default,
  Slider: slider_default,
  Switch: switch_default,
  Tooltip: tooltip_default,
  Input: inputTheme,
  PinInput: pinInputTheme,
  FormLabel: formLabelTheme,
  NumberInput: numberInputTheme,
  Select: nativeSelectTheme,
  Textarea: textareaTheme
};

// src/theme.ts
var theme = extendTheme(
  {
    colors: colors_default,
    fonts,
    fontSizes,
    textStyles,
    sizes: sizes_default,
    components: components_default,
    shadows: shadows_default,
    semanticTokens: semantic_tokens_default
  },
  withDefaultColorScheme({
    colorScheme: "primary",
    components: ["Radio", "Switch", "Checkbox"]
  })
);
export {
  theme
};
//# sourceMappingURL=index.mjs.map